import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import electronicPrescriptionService from '../../Service/ElectronicPrescriptionService';
import ElectronicPrescriptionPDF from '../../ElectronicPrescriptionPDF';

const FinalConfirmation = ({ 
    prescriptionData, 
    selectedPatient, 
    onNewPrescription,
    onShowPrescriptionsTable 
}) => {
    const navigate = useNavigate();
    const [isVisible, setIsVisible] = useState(false);
    const [emailSent, setEmailSent] = useState(false);
    const [patientHistory, setPatientHistory] = useState([]);
    const [doctorStats, setDoctorStats] = useState(null);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [isLoadingStats, setIsLoadingStats] = useState(false);
    const [statsError, setStatsError] = useState(false);
    const [showPDF, setShowPDF] = useState(false);

    // Mostrar solo si hay datos de prescripción exitosa
    useEffect(() => {
        if (prescriptionData && prescriptionData.success) {
            setIsVisible(true);
            loadPatientHistory();
            loadDoctorStats();
        }
    }, [prescriptionData]);

    // Cargar historial del paciente
    const loadPatientHistory = async () => {
        if (!selectedPatient?._id) return;
        
        setIsLoadingHistory(true);
        try {
            const response = await electronicPrescriptionService.getElectronicPrescriptionsByPatient(
                selectedPatient._id,
                { limit: 5, sortBy: 'createdAt', sortOrder: 'desc' }
            );
            
            if (response.data?.electronicPrescriptions) {
                setPatientHistory(response.data.electronicPrescriptions);
            }
        } catch (error) {
            console.error('Error cargando historial del paciente:', error);
            setPatientHistory([]);
        } finally {
            setIsLoadingHistory(false);
        }
    };

    const loadDoctorStats = async () => {
        setIsLoadingStats(true);
        setStatsError(false);
        
        try {
            console.log('Cargando estadísticas generales del sistema...');
            
            // DELAY para asegurar que la nueva receta esté guardada en el servidor
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            // Obtener TODAS las recetas del sistema
            const allPrescriptionsResponse = await electronicPrescriptionService.getAllElectronicPrescriptions({
                limit: 10000
            });
            
            let allPrescriptions = allPrescriptionsResponse.data?.electronicPrescriptions || [];
            
            console.log(`Total recetas obtenidas del servidor: ${allPrescriptions.length}`);
            console.log('Primeras 3 prescripciones para debug:', allPrescriptions.slice(0, 3));
            
            // AGREGAR LA RECETA ACTUAL SI NO ESTÁ EN LA LISTA
            if (prescriptionData?.success && prescriptionData.prescriptionId) {
                const currentPrescriptionExists = allPrescriptions.some(p => 
                    p.prescriptionId === prescriptionData.prescriptionId ||
                    p._id === prescriptionData._id
                );
                
                if (!currentPrescriptionExists) {
                    console.log('Agregando receta actual que no está en el servidor aún:', prescriptionData.prescriptionId);
                    const currentPrescription = {
                        _id: prescriptionData._id || `temp-${Date.now()}`,
                        prescriptionId: prescriptionData.prescriptionId,
                        patientInfo: {
                            userId: {
                                _id: selectedPatient?._id
                            }
                        },
                        medication: {
                            nombre: prescriptionData.medication?.nombre
                        },
                        createdAt: new Date().toISOString()
                    };
                    allPrescriptions.unshift(currentPrescription);
                    console.log('Receta actual agregada. Total ahora:', allPrescriptions.length);
                }
            }
            
            // 1. TOTAL DE RECETAS
            const totalPrescriptions = allPrescriptions.length;
            
            // 2. TOTAL DE PACIENTES ÚNICOS Y CONTEO DE MEDICAMENTOS
            const uniquePatients = new Set();
            const medicationCounts = {};
            
            allPrescriptions.forEach((prescription, index) => {
                console.log(`Procesando receta ${index + 1}:`, prescription.prescriptionId || prescription._id);
                
                // MÚLTIPLES INTENTOS para encontrar el ID del paciente
                let patientId = null;
                
                if (prescription.patientInfo?.userId?._id) {
                    patientId = prescription.patientInfo.userId._id;
                } else if (prescription.patientInfo?.userId && typeof prescription.patientInfo.userId === 'string') {
                    patientId = prescription.patientInfo.userId;
                } else if (prescription.patientId) {
                    patientId = prescription.patientId;
                } else if (prescription.patient?._id) {
                    patientId = prescription.patient._id;
                }
                
                if (patientId) {
                    const normalizedPatientId = String(patientId).trim();
                    uniquePatients.add(normalizedPatientId);
                    console.log(`Paciente ${index + 1}:`, normalizedPatientId);
                } else {
                    console.warn(`No se pudo encontrar ID de paciente en receta ${index + 1}:`, prescription);
                }
                
                // Contar medicamentos
                const medicationName = prescription.medication?.nombre?.trim() || 'Medicamento no especificado';
                const previousCount = medicationCounts[medicationName] || 0;
                medicationCounts[medicationName] = previousCount + 1;
                console.log(`Medicamento: ${medicationName} -> conteo: ${previousCount + 1}`);
            });
            
            // 3. TOP 3 MEDICAMENTOS MÁS RECETADOS
            const topMedications = Object.entries(medicationCounts)
                .map(([name, count]) => ({ name, count }))
                .sort((a, b) => b.count - a.count)
                .slice(0, 3);
            
            const generalStats = {
                totalPrescriptions,
                totalPatients: uniquePatients.size,
                topMedications
            };
            
            console.log('=== ESTADÍSTICAS FINALES ===');
            console.log('Total recetas:', totalPrescriptions);
            console.log('Total pacientes únicos:', uniquePatients.size);
            console.log('IDs de pacientes únicos:', Array.from(uniquePatients));
            console.log('Conteo completo de medicamentos:', medicationCounts);
            console.log('Top 3 medicamentos:', topMedications);
            
            setDoctorStats(generalStats);
            
        } catch (error) {
            console.error('Error cargando estadísticas generales:', error);
            setStatsError(true);
            
            setDoctorStats({
                totalPrescriptions: 0,
                totalPatients: 0,
                topMedications: []
            });
            
        } finally {
            setIsLoadingStats(false);
        }
    };

    const handleGeneratePDF = () => {
        console.log('Preparando datos para PDF...', prescriptionData);
        setShowPDF(true);
    };

    const handleClosePDF = () => {
        setShowPDF(false);
    };

    const handleNewPrescription = () => {
        if (onNewPrescription) {
            onNewPrescription();
        } else {
            navigate('/electronic-prescription', { 
                state: selectedPatient ? {
                    patientId: selectedPatient._id,
                    patientName: `${selectedPatient.name || ''} ${selectedPatient.surname || ''}`.trim(),
                    patientDob: selectedPatient.dateOfBirth,
                    patientEmail: selectedPatient.email,
                    patientPhone: selectedPatient.phone
                } : null 
            });
        }
    };

    const handleBackToPatients = () => {
        navigate('/patients/list');
    };

    const handleSendEmail = async () => {
        try {
            await new Promise(resolve => setTimeout(resolve, 1500));
            setEmailSent(true);
            setTimeout(() => setEmailSent(false), 3000);
        } catch (error) {
            console.error('Error enviando email:', error);
        }
    };

    const handleShowAllPrescriptions = () => {
        if (onShowPrescriptionsTable) {
            onShowPrescriptionsTable();
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('es-ES');
        } catch {
            return 'N/A';
        }
    };

    const getStatusBadge = (status) => {
        return electronicPrescriptionService.getStatusBadge(status);
    };

    const getReadableStatus = (status) => {
        return electronicPrescriptionService.getReadableStatus(status);
    };

    if (!isVisible || !prescriptionData?.success) {
        return null;
    }

    const patientName = selectedPatient?.name && selectedPatient?.surname 
        ? `${selectedPatient.name} ${selectedPatient.surname}` 
        : selectedPatient?.name || 'Paciente';

    return (
        <div className="card shadow-sm mb-4">
            <div className="card-body p-4">
                {/* Mensaje de éxito */}
                <div className="alert alert-success border rounded-3 text-center mb-4">
                    <div className="display-1 mb-3">✅</div>
                    <h4 className="alert-heading mb-3">¡Receta emitida correctamente!</h4>
                    <p className="mb-0">
                        La receta electrónica para <strong>{patientName}</strong> ha sido firmada y emitida correctamente.<br />
                        El paciente ha sido notificado y podrá retirar su medicación en cualquier farmacia autorizada.<br />
                        La receta está disponible en el portal del paciente y a través de la app SaludTI.
                    </p>

                    {/* Mensaje de email enviado */}
                    {emailSent && (
                        <div className="alert alert-info mt-3 mb-0">
                            📧 Correo electrónico enviado correctamente al paciente
                        </div>
                    )}

                    <div className="d-flex flex-wrap justify-content-center gap-2 mt-4">
                        <button 
                            className="btn btn-primary"
                            onClick={handleGeneratePDF}
                        >
                            Generar PDF
                        </button>
                        <button 
                            className="btn btn-primary"
                            onClick={handleNewPrescription}
                        >
                            Nueva receta
                        </button>
                        <button 
                            className="btn btn-secondary"
                            onClick={handleBackToPatients}
                        >
                            Volver a pacientes
                        </button>
                        <button 
                            className="btn btn-secondary"
                            onClick={handleSendEmail}
                            disabled={emailSent}
                        >
                            {emailSent ? 'Email enviado' : 'Enviar por email'}
                        </button>
                        <button 
                            className="btn btn-secondary"
                            onClick={handleShowAllPrescriptions}
                        >
                            Ver todas mis recetas
                        </button>
                    </div>
                </div>

                {/* Información adicional */}
                <h5 className="text-primary mb-3">Información adicional</h5>
                <div className="row g-3">
                    {/* Historial de recetas */}
                    <div className="col-md-6">
                        <div className="card h-100">
                            <div className="card-header bg-primary bg-opacity-10 text-primary fw-semibold">
                                Historial de recetas del paciente
                            </div>
                            <div className="card-body">
                                {isLoadingHistory ? (
                                    <div className="text-center text-muted py-4">
                                        Cargando historial...
                                    </div>
                                ) : patientHistory.length > 0 ? (
                                    <div className="list-group list-group-flush">
                                        {patientHistory.map((prescription, index) => (
                                            <div key={prescription._id || index} className="list-group-item px-0">
                                                <div className="fw-semibold mb-1">
                                                    {prescription.medication?.nombre || 'Medicamento no especificado'} 
                                                    {prescription.createdAt && <small className="text-muted"> ({formatDate(prescription.createdAt)})</small>}
                                                </div>
                                                <div className="small text-secondary mb-2">
                                                    {prescription.posology?.cantidad || '1'} {prescription.posology?.unidad || 'comprimido'} {prescription.posology?.frecuencia || 'según indicación médica'}
                                                    {prescription.treatmentDuration && ` durante ${prescription.treatmentDuration.value} ${prescription.treatmentDuration.unit?.toLowerCase() || 'días'}`}
                                                </div>
                                                <div className="small">
                                                    <span className={`badge ${getStatusBadge(prescription.status)}`}>
                                                        {getReadableStatus(prescription.status)}
                                                    </span>
                                                    {prescription.prescriberInfo?.fullName && (
                                                        <span className="ms-2">{prescription.prescriberInfo.fullName}</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center text-muted py-4">
                                        No hay historial previo de recetas para este paciente
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Estadísticas */}
                    <div className="col-md-6">
                        <div className="card h-100">
                            <div className="card-header bg-primary bg-opacity-10 text-primary fw-semibold">
                                Estadísticas de prescripción
                            </div>
                            <div className="card-body">
                                {isLoadingStats ? (
                                    <div className="text-center text-muted py-4">
                                        Cargando estadísticas...
                                    </div>
                                ) : doctorStats ? (
                                    <>
                                        <div className="mb-4">
                                            <h6 className="fw-semibold mb-3">Sus prescripciones del mes</h6>
                                            <div className="row g-3">
                                                <div className="col-6">
                                                    <div className="text-center p-3 bg-primary bg-opacity-10 rounded">
                                                        <div className="display-5 fw-bold text-primary">
                                                            {doctorStats.totalPrescriptions || 0}
                                                        </div>
                                                        <div className="small text-muted">Recetas emitidas</div>
                                                    </div>
                                                </div>
                                                <div className="col-6">
                                                    <div className="text-center p-3 bg-primary bg-opacity-10 rounded">
                                                        <div className="display-5 fw-bold text-primary">
                                                            {doctorStats.totalPatients || 0}
                                                        </div>
                                                        <div className="small text-muted">Pacientes atendidos</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {doctorStats.topMedications && doctorStats.topMedications.length > 0 ? (
                                            <div>
                                                <h6 className="fw-semibold mb-3">Medicamentos más recetados</h6>
                                                <div className="d-flex flex-column gap-2">
                                                    {doctorStats.topMedications.slice(0, 3).map((medication, index) => {
                                                        const maxCount = Math.max(...doctorStats.topMedications.map(m => m.count));
                                                        const percentage = maxCount > 0 ? (medication.count / maxCount) * 100 : 0;
                                                        
                                                        return (
                                                            <div key={index} className="d-flex align-items-center gap-2">
                                                                <div className="flex-grow-1 small text-truncate">
                                                                    {medication.name || `Medicamento ${index + 1}`}
                                                                </div>
                                                                <div className="flex-shrink-0" style={{ width: '100px' }}>
                                                                    <div className="progress" style={{ height: '8px' }}>
                                                                        <div 
                                                                            className="progress-bar bg-primary" 
                                                                            role="progressbar" 
                                                                            style={{ width: `${percentage}%` }}
                                                                            aria-valuenow={percentage}
                                                                            aria-valuemin="0"
                                                                            aria-valuemax="100"
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="fw-bold text-primary" style={{ minWidth: '30px', textAlign: 'right' }}>
                                                                    {medication.count}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-center text-muted py-4">
                                                Sin datos suficientes para mostrar medicamentos frecuentes
                                            </div>
                                        )}

                                        {/* DEBUG INFO (remover en producción) */}
                                        {process.env.NODE_ENV === 'development' && (
                                            <div className="alert alert-secondary small mt-3 mb-0">
                                                <strong>Debug Info:</strong><br />
                                                Error: {statsError ? 'Sí' : 'No'}<br />
                                                Datos cargados: {doctorStats ? 'Sí' : 'No'}<br />
                                                Total medicamentos: {doctorStats?.topMedications?.length || 0}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className={`text-center py-4 ${statsError ? 'text-danger' : 'text-muted'}`}>
                                        {statsError ? 'Error cargando estadísticas' : 'Sin estadísticas disponibles'}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* MODAL PDF */}
            {showPDF && (
                <ElectronicPrescriptionPDF
                    prescriptionData={prescriptionData}
                    selectedPatient={prescriptionData.selectedPatient || selectedPatient}
                    selectedDoctor={prescriptionData.selectedDoctor}
                    medicationDetails={prescriptionData.medicationDetails}
                    prescriptionConfig={prescriptionData.prescriptionConfig}
                    onClose={handleClosePDF}
                />
            )}
        </div>
    );
};

export default FinalConfirmation;